import { useLocation } from 'react-router-dom';

import { useEdition } from '@/editions';
import type { Item, Section } from '@/editions/types';
import { useActiveCompany } from '@/features/company/CompanyContext';
import { ITEM_MODULES } from '@/features/company/modules';
import { useMe } from '@/features/me/MeContext';

export interface VisibleSection extends Section {
  visibleItems: readonly Item[];
  active: boolean;
}

/** The caller's edition sections, narrowed by `visible` (permissions) and by
 * the active company's module toggles (PLAN-FASE3.md WP-06 — `ITEM_MODULES`
 * maps an item id to its module key; an item with no entry there has no
 * module and is never hidden this way), and annotated with whether the
 * current route is inside each one (§ Hoofdmenu: "Een sectie is actief als
 * de huidige route met een van haar prefixen begint"). Shared by `Sidebar`
 * and `MobileDrawer` (via `SidebarNav`) so the two surfaces never disagree
 * about what is on offer. */
export function useVisibleSections(): readonly VisibleSection[] {
  const edition = useEdition();
  const { permissions } = useMe();
  const { isModuleEnabled } = useActiveCompany();
  const location = useLocation();

  return edition.sections
    .filter((section) => !section.visible || section.visible(permissions))
    .map((section) => ({
      ...section,
      visibleItems: (section.items ?? []).filter((item) => {
        if (item.visible && !item.visible(permissions)) return false;
        const moduleKey = ITEM_MODULES[item.id];
        return !moduleKey || isModuleEnabled(moduleKey);
      }),
      active: section.match.some((prefix) => location.pathname.startsWith(prefix)),
    }));
}
